'use client';

import React, { useState } from 'react';
import { Trash2, Tag, TrendingUp, FolderOpen, X, ChevronDown } from 'lucide-react';

interface BulkActionBarProps {
  selectedCount: number;
  onClear: () => void;
}

export default function BulkActionBar({ selectedCount, onClear }: BulkActionBarProps) {
  const [statusDropdown, setStatusDropdown] = useState(false);
  const [priceDropdown, setPriceDropdown] = useState(false);

  if (selectedCount === 0) return null;

  return (
    <div className="slide-up fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-foreground text-white rounded-2xl shadow-modal px-5 py-3 flex items-center gap-4 min-w-[520px]">
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs font-bold">
          {selectedCount}
        </span>
        <span className="text-sm font-medium">products selected</span>
      </div>

      <div className="h-5 w-px bg-white/20" />

      {/* Change status */}
      <div className="relative">
        <button
          onClick={() => { setStatusDropdown(!statusDropdown); setPriceDropdown(false); }}
          className="flex items-center gap-1.5 text-sm font-medium hover:text-white/80 transition-colors"
        >
          <Tag size={14} />
          Change Status
          <ChevronDown size={12} />
        </button>
        {statusDropdown && (
          <div className="absolute bottom-full mb-2 left-0 bg-card border border-border rounded-xl shadow-panel py-1 w-44 fade-in">
            {['Published', 'Draft', 'Archived'].map((s) => (
              <button
                key={`bulk-status-${s.toLowerCase()}`}
                className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                onClick={() => setStatusDropdown(false)}
              >
                Set as {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Update price */}
      <div className="relative">
        <button
          onClick={() => { setPriceDropdown(!priceDropdown); setStatusDropdown(false); }}
          className="flex items-center gap-1.5 text-sm font-medium hover:text-white/80 transition-colors"
        >
          <TrendingUp size={14} />
          Update Price
          <ChevronDown size={12} />
        </button>
        {priceDropdown && (
          <div className="absolute bottom-full mb-2 left-0 bg-card border border-border rounded-xl shadow-panel py-2 px-3 w-52 fade-in">
            <p className="text-xs font-semibold text-foreground mb-2">Price Adjustment</p>
            <div className="flex gap-2 mb-2">
              <button className="flex-1 text-xs py-1.5 rounded-lg bg-muted text-foreground font-medium hover:bg-border transition-colors">% Change</button>
              <button className="flex-1 text-xs py-1.5 rounded-lg bg-primary text-white font-medium">Fixed Amount</button>
            </div>
            <div className="flex gap-2">
              <input type="number" placeholder="Amount (PKR)" className="input-field text-xs h-8 flex-1" />
              <button className="btn-primary text-xs px-3 h-8">Apply</button>
            </div>
          </div>
        )}
      </div>

      {/* Move category */}
      <button className="flex items-center gap-1.5 text-sm font-medium hover:text-white/80 transition-colors">
        <FolderOpen size={14} />
        Move Category
      </button>

      {/* Delete */}
      <button className="flex items-center gap-1.5 text-sm font-medium text-red-400 hover:text-red-300 transition-colors">
        <Trash2 size={14} />
        Delete
      </button>

      <div className="h-5 w-px bg-white/20" />

      <button
        onClick={onClear}
        className="text-white/60 hover:text-white transition-colors"
        title="Deselect all"
      >
        <X size={16} />
      </button>
    </div>
  );
}